import { Button } from "$components/button/button";
import { ErrorBoundary } from "$components/error-boundary";
import { PlusIconBorderless } from "$components/icons";
import { makeStyles } from "$components/mui-styles";
import { useConfig } from "$global-providers/config-provider/provider";
import { useUser } from "$global-providers/user-provider";
import { INSURANCE_PROVIDERS } from "$routes/new/patient/step3/_lib/insurance/insurance-providers/insurance-providers";
import { getCardId } from "$routes/patient/_lib/get-card-id";
import { getInsuranceNumberForDisplay } from "$routes/patient/_lib/insurance";
import { usePatientDetailsQuery } from "$routes/patient/[:patientId]/_lib/__generated__/patient-queries.gql";
import { InsuranceValidityChip } from "$routes/patient/[:patientId]/_lib/patient-info-dropdown/insurance-status";
import { Box, styled, Typography } from "@material-ui/core";
import { Role } from "elephant-web/gql";
import { useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { InsuranceForm } from "./insurance-form";

const useStyles = makeStyles((theme) => ({
  titleContainer: {
    display: "block",
    borderBottom: "solid 1px gray",
    borderBottomColor: theme.palette.grey[300],
    paddingBottom: theme.spacing(1),
    alignItems: "center",
    marginBottom: theme.spacing(3),
  },
  insurances: {
    display: "flex",
    flexDirection: "column",
    marginBottom: theme.spacing(3),
  },
  insuranceType: {
    marginRight: theme.spacing(1),
  },
}));

const InsuranceTypeAndValidity = styled("div")({
  display: "flex",
  alignItems: "center",
});
const InsuranceNumber = styled(Typography)(({ theme }) => ({
  color: theme.palette.grey[700],
}));

export const InsuranceTabContent: FC<{ uuid: string }> = ({ uuid }) => {
  const classes = useStyles();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [cardId, setCardId] = useState("");
  const { territory } = useConfig();
  const { formatMessage } = useIntl();
  const { role } = useUser();

  const insuranceProviders = INSURANCE_PROVIDERS[territory];

  const findPatientById = usePatientDetailsQuery({
    variables: {
      uuid,
      searchWholeTerritory: true,
    },
  });

  useEffect(() => {
    if (findPatientById?.data?.patientByCardId?.patient) {
      const cardNumber = getCardId(findPatientById?.data);
      setCardId(cardNumber);
    }
  }, [findPatientById?.data]);

  const patientDetails = findPatientById.data?.patientByCardId?.patient;
  const patientWithCard = { patient: { ...patientDetails, cardId } };
  const insuranceType = patientWithCard?.patient?.insurance?.insuranceType;
  const insuranceNumber = getInsuranceNumberForDisplay(
    patientWithCard?.patient?.insurance,
  );
  const currVisit = patientWithCard?.patient?.currentVisit;

  return (
    <Box>
      <Box className={classes.titleContainer}>
        <Typography variant="h4">
          <FormattedMessage
            id="patient.insurance.sidebar.details"
            defaultMessage="Insurance Details"
          />
        </Typography>
        {!isFormOpen && !insuranceType && (
          <Typography variant="body2" data-testid="sidebar-no-insurance">
            <FormattedMessage
              id="patient.insurance.sidebar.noInsurance"
              defaultMessage="This patient has no insurance"
            />
          </Typography>
        )}
      </Box>
      {!isFormOpen && insuranceType && (
        <Box className={classes.insurances}>
          <InsuranceTypeAndValidity>
            <Typography
              variant="h6"
              className={classes.insuranceType}
              data-testid="sidebar-insurance-type"
            >
              {insuranceType}
            </Typography>
            <InsuranceValidityChip
              insurance={patientDetails?.insurance}
              currentVisitInsurances={currVisit?.insuranceList}
              data-testid="sidebar-chip"
            />
          </InsuranceTypeAndValidity>
          <InsuranceNumber variant="body2">
            {formatMessage(
              {
                id: "patient.insurance.sidebar.insuranceNumber",
                defaultMessage: "Insurance no: {insuranceNumber}",
              },
              { insuranceNumber },
            )}
          </InsuranceNumber>
        </Box>
      )}

      {isFormOpen && insuranceProviders && (
        <InsuranceForm
          insuranceProviders={insuranceProviders}
          patient={patientWithCard}
          setOpen={setIsFormOpen}
        />
      )}
      {role !== Role.Sonographer && (
        <Box>
          {!isFormOpen &&
            (insuranceType ? (
              <Button
                color="primary"
                onClick={() => setIsFormOpen(true)}
                data-testid="sidebar-check-insurance"
              >
                <FormattedMessage
                  id="patient.insurance.sidebar.add"
                  defaultMessage="Check Insurance"
                />
              </Button>
            ) : (
              <Button
                color="secondary"
                data-testid="sidebar-add-insurance"
                startIcon={<PlusIconBorderless />}
                onClick={() => setIsFormOpen(true)}
              >
                <FormattedMessage
                  id="patient.insurance.sidebar.check"
                  defaultMessage="Add Insurance"
                />
              </Button>
            ))}
        </Box>
      )}
    </Box>
  );
};

export const InsuranceTab: FC<{ uuid: string }> = ({ uuid }) => {
  return (
    <ErrorBoundary
      name="insurance-tab"
      fallback={() => <div>An error occurred</div>}
    >
      <InsuranceTabContent uuid={uuid} />
    </ErrorBoundary>
  );
};
